import { PRODUCT_NAME } from "@kuroo/brand";
import type { WebAuthnPublicKeyOptions } from "@kuroo/sdk";

export function base64UrlBytes(value: string): ArrayBuffer {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
  const bytes = Uint8Array.from(atob(padded), (character) => character.charCodeAt(0));
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

export function base64UrlValue(value: ArrayBuffer): string {
  const bytes = new Uint8Array(value);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

export function browserRequestOptions(options: WebAuthnPublicKeyOptions): PublicKeyCredentialRequestOptions {
  return {
    challenge: base64UrlBytes(options.challenge),
    timeout: options.timeout,
    userVerification: options.userVerification,
    rpId: options.rp?.id,
    allowCredentials: options.allowCredentials?.map((credential) => ({ id: base64UrlBytes(credential.id), type: "public-key" as const, transports: credential.transports }))
  };
}

export function browserCreationOptions(options: WebAuthnPublicKeyOptions): PublicKeyCredentialCreationOptions {
  if (!options.user) throw new Error("La API no devolvió el usuario WebAuthn.");
  return {
    challenge: base64UrlBytes(options.challenge),
    rp: options.rp ? { id: options.rp.id, name: options.rp.name } : { name: PRODUCT_NAME },
    user: { id: base64UrlBytes(options.user.id), name: options.user.name, displayName: options.user.displayName },
    pubKeyCredParams: options.pubKeyCredParams,
    timeout: options.timeout,
    authenticatorSelection: { userVerification: options.userVerification },
    attestation: options.attestation,
    excludeCredentials: options.excludeCredentials?.map((credential) => ({ id: base64UrlBytes(credential.id), type: "public-key" as const, transports: credential.transports }))
  };
}

export function assertionPayload(credential: PublicKeyCredential): Record<string, unknown> {
  const response = credential.response as AuthenticatorAssertionResponse;
  return { credentialId: credential.id, authenticatorData: base64UrlValue(response.authenticatorData), clientDataJSON: base64UrlValue(response.clientDataJSON), signature: base64UrlValue(response.signature), userHandle: response.userHandle ? base64UrlValue(response.userHandle) : undefined };
}

export function attestationPayload(credential: PublicKeyCredential): Record<string, unknown> {
  const response = credential.response as AuthenticatorAttestationResponse & { getAuthenticatorData?: () => ArrayBuffer };
  return { credentialId: credential.id, attestationObject: base64UrlValue(response.attestationObject), clientDataJSON: base64UrlValue(response.clientDataJSON), authenticatorData: response.getAuthenticatorData ? base64UrlValue(response.getAuthenticatorData()) : undefined };
}
